import { apiErrorMessage } from '@agos/api-client'
import type { FiskalSettings } from '@agos/api-client/model'
import {
  getGetFiskalRegistersQueryKey,
  useDeleteFiskalRegistersKasaId,
  useGetFiskalRegisters,
  usePostFiskalRegisters,
} from '@agos/api-client/fiskal'
import {
  getGetSettingsFiskalQueryKey,
  useGetSettingsFiskal,
  usePostFiskalEkasaUnblock,
  usePostFiskalRaw,
  usePostFiskalReceipt,
  usePostFiskalRecover,
  usePostFiskalResetBreaker,
  usePostFiskalTest,
  usePutSettingsFiskal,
  usePutSettingsFiskalShared,
} from '@agos/api-client/fiskal'
import { T, tt } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  ConfirmDialog,
  PageHeader,
  Spinner,
  StatusBadge,
  TabGroup,
  cn,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowUpRight,
  BookOpen,
  Building2,
  CheckCircle2,
  Info,
  Monitor,
  Plus,
  Receipt,
  RefreshCw,
  Save,
  Truck,
  Utensils,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  DIAGNOSTIC_COMMANDS,
  effectOf,
  needsConfirmation,
  stampUid,
  type CommandEffect,
  type DiagnosticCommand,
} from './raw-commands'
import {
  CARD_SLOTS,
  NOTERM_SLOTS,
  cardSlotWarning,
  canAddRegister,
  connectionPayload,
  newRegisterForm,
  newRegisterNote,
  newRegisterPayload,
  newRegisterProblems,
  readConnection,
  readShared,
  type CardSlot,
  type ConnectionForm,
  type KasaId,
  type NewRegisterForm,
  type SharedForm,
} from './settings-form'

/**
 * `/fiskal` — the two registers' configuration.
 *
 * Everything on this screen ends up on a tax document, so the port is faithful to
 * legacy's layout and adds three guards legacy does not have:
 *
 *  - **FB-070, and it is no longer on this screen - §9.327.** The VAT rate pickers
 *    were built here from the franchise's own `vat-categories` rather than a
 *    hardcoded list from before the 2025 rates; two of legacy's five presets are
 *    wrong against this franchise's configuration and the rate it actually charges
 *    as standard was not offered at all. **The owner asked for one place** and the
 *    pickers, the guard and their tests moved whole to
 *    `manager-settings/vat-categories/`. This screen shows the three stored rates
 *    and links there.
 *  - **FB-071.** The *Test dokladu (1,00 €)* button issues a **real fiscal
 *    document** through the register — a tax document for a euro nobody paid, which
 *    then has to be storned. Legacy fires it on one click with no warning. It is
 *    confirmed here, and the confirmation says what it does.
 *  - **FB-072.** Three of the eight diagnostics presets change the device or charge
 *    a card; legacy renders them identically to `FRSTAT`, which asks a question.
 *    They are classified and confirmed. Detail in `raw-commands.ts`.
 *
 * Ported from legacy `pages/fiskal-settings.tsx`.
 */

export function FiskalSettingsPage() {
  // §10A.35 — the configured registers, falling back to the two the port shipped with until the
  // call answers so the page is never tab-less.
  const { data: registerData } = useGetFiskalRegisters()
  const registerTabs = useMemo(() => {
    const rows = Array.isArray(registerData)
      ? (registerData as Array<{ kasaId?: string; label?: string }>)
      : []
    const configured = rows.flatMap((row) =>
      row.kasaId === undefined || row.kasaId === ''
        ? []
        : [
            {
              id: row.kasaId,
              label: row.label ?? row.kasaId,
              icon: row.kasaId === 'rozvoz' ? Truck : Utensils,
            },
          ],
    )
    return configured.length > 0
      ? configured
      : [
          { id: 'restauracia', label: tt('Kasa Reštaurácia'), icon: Utensils },
          { id: 'rozvoz', label: tt('Kasa Rozvoz'), icon: Truck },
        ]
  }, [registerData])

  const [activeKasa, setActiveKasa] = useState<KasaId>('restauracia')
  const { toast } = useToast()
  const queryClient = useQueryClient()
  // §9.106 — adding a register. The tabs above have always drawn whatever `GET /fiskal/registers`
  // returns; until §10A.65 nothing could put a third row in it.
  const [adding, setAdding] = useState<NewRegisterForm | null>(null)
  const existingIds = registerTabs.map((tab) => tab.id)
  const activeTab = registerTabs.find((tab) => tab.id === activeKasa)
  const problems = adding === null ? [] : newRegisterProblems(adding, existingIds)
  const refreshRegisters = () =>
    queryClient.invalidateQueries({ queryKey: getGetFiskalRegistersQueryKey() })

  const addRegister = usePostFiskalRegisters({
    mutation: {
      onSuccess: (_result, variables) => {
        const created = String((variables.data as { kasaId?: string }).kasaId ?? '')
        void refreshRegisters()
        setAdding(null)
        // Straight to the new register: it is switched off and has no address, and that is the next
        // thing the person has to do.
        if (created !== '') setActiveKasa(created as KasaId)
        toast({ title: tt('Kasa pridaná'), description: tt('Je vypnutá, kým nezadáte pripojenie.') })
      },
      onError: (error) =>
        toast({ variant: 'destructive', title: apiErrorMessage(error, tt('Kasu sa nepodarilo pridať')) }),
    },
  })

  const removeRegister = useDeleteFiskalRegistersKasaId({
    mutation: {
      onSuccess: () => {
        void refreshRegisters()
        setActiveKasa('restauracia')
        toast({ title: tt('Kasa odstránená') })
      },
      // The server refuses while it has uzávierky, while a channel points at it, or when it is the
      // last one — and its message says which, so it is shown rather than replaced.
      onError: (error) =>
        toast({ variant: 'destructive', title: apiErrorMessage(error, tt('Kasu sa nepodarilo odstrániť')) }),
    },
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Pokladňa')}
        description={tt('Prepojenie s fiskálnymi tlačiarňami pre výtlačky dokladov')}
        icon={<Receipt className="w-5 h-5" />}
      />

      <TabGroup
        variant="bar"
        // §10A.35 step 4 — a tab per register the franchise has configured, so a third can have its
        // connection settings edited rather than only being routed to. The two the port shipped with
        // keep their glyphs; anything the franchise adds gets the till, which is what it is.
        tabs={registerTabs}
        active={activeKasa}
        onChange={(id) => setActiveKasa(id as KasaId)}
      />

      <div className="flex flex-wrap items-center gap-2" data-register-controls>
        <button
          type="button"
          onClick={() => setAdding(adding === null ? newRegisterForm() : null)}
          data-add-register
          className={cn(BUTTON_SECONDARY, 'flex items-center gap-2 px-3 py-2 rounded-xl text-xs')}
        >
          <Plus className="w-3.5 h-3.5" />
          <T>Pridať kasu</T>
        </button>
        {/* Only where it can succeed. The server refuses a register with uzávierky against it, one a
          * channel points at, and the last one — and a button whose only outcome is a refusal is
          * worse than no button. */}
        {registerTabs.length > 1 && (
          <button
            type="button"
            onClick={() => removeRegister.mutate({ kasaId: activeKasa })}
            disabled={removeRegister.isPending}
            data-remove-register={activeKasa}
            className="px-3 py-2 rounded-xl text-xs text-muted-foreground hover:text-red-400 transition-colors"
          >
            <T>Odstrániť túto kasu</T>
          </button>
        )}
      </div>

      {adding !== null && (
        <section className="rounded-xl border border-border bg-card p-4 space-y-3" data-add-register-form>
          <h2 className="text-sm font-bold">
            <T>Nová kasa</T>
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">
                <T>Id</T>
              </span>
              <input
                value={adding.kasaId}
                onChange={(event) => setAdding({ ...adding, kasaId: event.target.value })}
                data-new-register-id
                placeholder="terasa"
                className="w-full min-h-[40px] rounded-xl bg-background border border-border px-3 text-sm outline-none focus:border-primary"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">
                <T>Názov</T>
              </span>
              <input
                value={adding.label}
                onChange={(event) => setAdding({ ...adding, label: event.target.value })}
                data-new-register-label
                placeholder={tt('Kasa Terasa')}
                className="w-full min-h-[40px] rounded-xl bg-background border border-border px-3 text-sm outline-none focus:border-primary"
              />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground" data-new-register-note>
            {newRegisterNote(problems)}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canAddRegister(adding, existingIds) || addRegister.isPending}
              onClick={() =>
                addRegister.mutate({ data: newRegisterPayload(adding) as never })
              }
              data-save-register
              className={cn(BUTTON_PRIMARY, 'px-4 py-2 rounded-xl text-sm disabled:opacity-40')}
            >
              <T>Pridať</T>
            </button>
            <button
              type="button"
              onClick={() => setAdding(null)}
              className="px-3 py-2 rounded-xl text-xs text-muted-foreground hover:text-foreground"
            >
              <T>Zrušiť</T>
            </button>
          </div>
        </section>
      )}

      <section
        className={cn(
          'rounded-xl border p-5 space-y-4',
          activeKasa === 'restauracia'
            ? 'bg-amber-500/5 border-amber-500/20'
            : 'bg-violet-500/5 border-violet-500/20',
        )}
      >
        {/* §9.106: this heading was a binary - anything that was not `restauracia` was drawn as
          * *Kasa Rozvoz*, with the delivery description under it. That is the same defect §10A.35
          * step 3 fixed on the server in `FiskalRegisters.label`, in a copy of the rule living here.
          * A third register was named after delivery and told the reader that dispatch prints on it
          * automatically, which is not true of it. The tab's own label is the one the server gave. */}
        <h2
          className={cn(
            'font-bold text-base flex items-center gap-2',
            activeKasa === 'restauracia' ? 'text-amber-400' : 'text-violet-400',
          )}
          data-kasa-heading={activeKasa}
        >
          {activeKasa === 'rozvoz' ? (
            <Truck className="w-5 h-5" />
          ) : (
            <Utensils className="w-5 h-5" />
          )}
          {`${activeTab?.label ?? activeKasa} - ${tt('pripojenie')}`}
        </h2>
        <p className="text-xs text-muted-foreground">
          {activeKasa === 'restauracia'
            ? tt('Nastavenia pripojenia pre kasu reštaurácie (platby pri stoloch).')
            : activeKasa === 'rozvoz'
              ? tt('Nastavenia pripojenia pre kasu rozvozu (dispečing tlačí automaticky).')
              : tt('Nastavenia pripojenia pre túto kasu.')}
        </p>
        <KasaSection key={activeKasa} kasaId={activeKasa} />
      </section>

      <section className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5 space-y-4">
        <h2 className="font-bold text-base text-emerald-400 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          <T>Zdieľané nastavenia</T>
        </h2>
        <p className="text-xs text-muted-foreground">
          <T>Sadzby DPH a údaje podniku platia pre obe kasy súčasne.</T>
        </p>
        <SharedSettingsSection />
      </section>
    </div>
  )
}

// ─── one register ────────────────────────────────────────────────────────────

function KasaSection({ kasaId }: { kasaId: KasaId }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useGetSettingsFiskal({ kasaId })
  const [form, setForm] = useState<ConnectionForm>(() => readConnection(undefined, kasaId))
  const [newApiKey, setNewApiKey] = useState('')

  // The server's answer is the form's starting point. Keyed on the payload, so a
  // refetch that changes nothing does not discard what is being typed.
  const [loadedFrom, setLoadedFrom] = useState<FiskalSettings | undefined>(undefined)
  if (data && data !== loadedFrom) {
    setLoadedFrom(data)
    setForm(readConnection(data, kasaId))
  }

  const hasApiKey = data?.hasApiKey === true
  const set = <K extends keyof ConnectionForm>(key: K, value: ConnectionForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const save = usePutSettingsFiskal({
    mutation: {
      onSuccess: () => {
        setNewApiKey('')
        void queryClient.invalidateQueries({ queryKey: getGetSettingsFiskalQueryKey() })
        toast({ title: tt('Nastavenia pripojenia uložené') })
      },
      onError: (error: unknown) => toast(failure(error)),
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-24">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4" data-kasa-section={kasaId}>
      <ToggleCard
        on={form.isEnabled}
        onChange={(next) => set('isEnabled', next)}
        icon={<Receipt className="w-5 h-5" />}
        tone="emerald"
        label={form.isEnabled ? tt('Kasa je aktívna') : tt('Kasa nie je aktívna')}
        hint={
          form.isEnabled
            ? tt('Fiskálne doklady sa budú vystavovať')
            : tt('Tlač fiskálnych dokladov je vypnutá')
        }
      />

      {form.isEnabled && (
        <ToggleCard
          on={form.virtualMode}
          onChange={(next) => set('virtualMode', next)}
          icon={<Monitor className="w-5 h-5" />}
          tone="violet"
          label={tt('Virtuálna kasa (bez fyzického zariadenia)')}
          hint={
            form.virtualMode
              ? tt(
                  'Aktívny mód: doklady sa tlačia priamo z prehliadača. Fiskal Pro zariadenie nie je potrebné.',
                )
              : tt(
                  'Vypnuté: používa sa reálne Fiskal Pro zariadenie cez sieť. Zapnite ak nemáte fiskálnu tlačiareň.',
                )
          }
        />
      )}

      {form.isEnabled && form.virtualMode && (
        <p className="flex items-start gap-2 text-xs text-violet-400">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          {/* §14.2 - the state this mode produces, said here rather than
              discovered on /fiskal-receipts. */}
          <T>
            Doklady z virtuálnej kasy nemajú UID, pretože ich daňové zariadenie nikdy nevidelo. Na
            zariadení ich preto nie je možné stornovať.
          </T>
        </p>
      )}

      {form.isEnabled && !form.virtualMode && (
        <div className="bg-card rounded-xl border border-border p-5 space-y-4">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Wifi className="w-4 h-4 text-muted-foreground" /> Pripojenie k Fiskal Pro
          </h3>

          <div className="space-y-3 border border-emerald-700/40 rounded-lg p-3 bg-emerald-950/20">
            <p className="text-xs text-emerald-300 font-semibold uppercase tracking-wider">
              <T>Priame TCP pripojenie (odporúčané)</T>
            </p>
            <Field
              label={tt('Kasa hostiteľ (DNS alebo IP)')}
              hint={tt(
                'Verejná adresa kasy. Napr. kasa.example.com alebo IP po port-forwarde z routera.',
              )}
            >
              <TextInput
                value={form.kasaHost}
                onChange={(value) => set('kasaHost', value)}
                placeholder="napr. kasa-bratislava.example.com"
                label={tt('Kasa hostiteľ')}
              />
            </Field>
            <Field label="Kasa port" hint={tt('Port FiskalPRO N5 (predvolene 6090).')}>
              <TextInput
                value={String(form.kasaPort)}
                onChange={(value) => set('kasaPort', Number(value) || 6090)}
                placeholder="6090"
                label={tt('Kasa port')}
              />
            </Field>

            <Field
              label={tt('Kartová platba - slot')}
              hint={tt(
                '„0" = bez FTCARDSTART (samostatný terminál), „1" = externý FTCARDSTART, „2" = interný FTCARDSTART (integrovaný terminál).',
              )}
            >
              <select
                value={form.cardPaymentSlot}
                aria-label={tt('Kartová platba - slot')}
                onChange={(event) => set('cardPaymentSlot', event.target.value as CardSlot)}
                className={SELECT_CLASSES}
              >
                {CARD_SLOTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {cardSlotWarning(form.cardPaymentSlot) && (
                <p
                  className="flex items-start gap-1.5 text-xs text-amber-500 mt-1"
                  data-card-slot-warning
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  {cardSlotWarning(form.cardPaymentSlot)}
                </p>
              )}
            </Field>

            {/* Legacy's fourth control, which AGOS could not store until `noterm_payment_slot`
                existed (MIGRATION-STATUS). The paragraph that stood here said so; it is a select now,
                and the hint is legacy's own explanation of what the index costs. */}
            <Field
              label={tt('Karta bez terminálu - FPAYI index')}
              hint={tt(
                'Doklad za kartu u kuriéra alebo online - peniaze sú už prijaté a kasa má len tlačiť. Ak má kasa na slote 2 integrovanú čítačku, pri tlači vyzve na priloženie už zaplatenej karty; vtedy zvoľte iný index.',
              )}
            >
              <select
                value={form.notermPaymentSlot}
                aria-label={tt('Karta bez terminálu - FPAYI index')}
                onChange={(event) => set('notermPaymentSlot', event.target.value)}
                data-noterm-slot
                className={SELECT_CLASSES}
              >
                {NOTERM_SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field
            label={tt('API kľúč (ak je nastavený v Fiskal Pro)')}
            hint={
              hasApiKey
                ? tt('API kľúč je uložený - vyplňte iba pre zmenu')
                : tt('Voliteľné - ak Fiskal Pro nevyžaduje autentifikáciu')
            }
          >
            <TextInput
              value={newApiKey}
              onChange={setNewApiKey}
              type="password"
              label={tt('API kľúč')}
              placeholder={hasApiKey ? tt('••••••••••• (nastavený)') : tt('Zadajte API kľúč')}
            />
          </Field>

          <Field
            label={tt('ID implementátora (FSIMPNR)')}
            hint={tt(
              'Voliteľné - kód implementátora systému podľa zmluvy s FiskalPRO. Ak je vyplnený, odošle sa príkazom FSIMPNR na začiatku každej komunikácie.',
            )}
          >
            <TextInput
              value={form.implementorId}
              onChange={(value) => set('implementorId', value)}
              placeholder="napr. IMPL123"
              label={tt('ID implementátora')}
            />
          </Field>

          {/* §9.193 - the HTTP bridge field is gone. It was never a transport: `KasaConnection` is a
              raw socket, `FiskalRegisters.targetFor` reads `kasaHost` and `kasaPort` and nothing
              else, and `serverUrl` has exactly one reader on the server - the response that echoes
              it back. Its hint said *"used while the register host is empty"*, and with that host
              empty the server refuses by name: *chýba kasaHost*. The stored value is untouched;
              `PUT /settings/fiskal` still accepts the field. */}

          <DeviceActions kasaId={kasaId} />
        </div>
      )}

      <button
        type="button"
        onClick={() =>
          save.mutate({ data: connectionPayload(form, kasaId, newApiKey) })
        }
        disabled={save.isPending}
        className="w-full flex items-center justify-center gap-2 bg-primary hover:opacity-90 disabled:opacity-50 text-primary-foreground font-semibold rounded-xl py-2.5 transition-all"
      >
        {save.isPending ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />}
        <T>Uložiť nastavenia pripojenia</T>
      </button>
    </div>
  )
}

// ─── the buttons that talk to the device ─────────────────────────────────────

interface PendingCommand {
  title: string
  description: string
  run: () => void
}

function DeviceActions({ kasaId }: { kasaId: KasaId }) {
  const { toast } = useToast()
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [output, setOutput] = useState<string | null>(null)
  const [rawCmd, setRawCmd] = useState('FRRTC')
  const [pending, setPending] = useState<PendingCommand | null>(null)

  const test = usePostFiskalTest({
    mutation: {
      onSuccess: (result: unknown) => {
        const ok = isOk(result)
        setTestResult({
          ok,
          message: ok
            ? tt('Pripojenie úspešné! Fiskal Pro reaguje.')
            : (errorText(result) ?? tt('Kasa neodpovedala.')),
        })
      },
      // §9.385 - **on a till test, the register's own words are the whole point.** This threw away
      // the sentence that says *which* register, *which* host and what it answered.
      onError: (caught) =>
        setTestResult({
          ok: false,
          message: apiErrorMessage(caught, tt('Nepodarilo sa spojiť so serverom.')),
        }),
    },
  })

  const recover = usePostFiskalRecover({
    mutation: {
      onSuccess: (result: unknown) =>
        toast({
          title: tt('Kasa odblokovaná'),
          description: messageText(result) ?? tt('Otvorený doklad bol zatvorený (FTCLOSE).'),
        }),
      onError: (error: unknown) => toast(failure(error)),
    },
  })

  const receipt = usePostFiskalReceipt({
    mutation: {
      onSuccess: (result: unknown) => setOutput(stringify(result)),
      onError: (error: unknown) => setOutput(stringify(error)),
    },
  })

  const raw = usePostFiskalRaw({
    mutation: {
      onSuccess: (result: unknown) => setOutput(stringify(result)),
      onError: (error: unknown) => setOutput(stringify(error)),
    },
  })

  const resetBreaker = usePostFiskalResetBreaker({
    mutation: {
      onSuccess: () =>
        toast({
          title: tt('Pripojenie resetované'),
          description: tt('Terminál je opäť aktívny.'),
        }),
      onError: (error: unknown) => toast(failure(error)),
    },
  })

  const unblock = usePostFiskalEkasaUnblock({
    mutation: {
      onSuccess: (result: unknown) => {
        const stillBlocked = readString(result, 'unblockCode') === 'STILL_BLOCKED'
        toast({
          title: stillBlocked ? tt('eKasa je stále zablokovaná') : tt('eKasa odblokovaná'),
          description: stillBlocked
            ? (errorText(result) ?? tt('Kontaktujte servis FiskalPRO.'))
            : (messageText(result) ?? tt('Odblokovanie prebehlo úspešne.')),
          variant: stillBlocked ? 'destructive' : undefined,
        })
      },
      onError: (error: unknown) => toast(failure(error)),
    },
  })

  /** FB-071/FB-072 — anything that is not a question is confirmed first. */
  const confirmThen = (command: PendingCommand) => setPending(command)

  const sendRaw = (command: string) => {
    const cmd = command.trim()
    if (!cmd) return
    setRawCmd(cmd)
    setOutput(null)
    const send = () => raw.mutate({ data: { kasaId, cmd } })
    if (!needsConfirmation(cmd)) {
      send()
      return
    }
    confirmThen({
      title: tt('Odoslať príkaz priamo do pokladne?'),
      description: `${cmd} - ${consequenceOf(cmd)}`,
      run: send,
    })
  }

  const sendPreset = (command: DiagnosticCommand) => {
    // The UID is stamped here, in the click. Legacy built it in the render body,
    // so the command changed on every repaint.
    sendRaw(command.effect === 'transacts' ? stampUid(command.cmd) : command.cmd)
  }

  return (
    <>
      <div className="pt-1 flex flex-wrap gap-2">
        <ActionButton
          onClick={() => {
            setTestResult(null)
            test.mutate({ data: { kasaId } })
          }}
          pending={test.isPending}
          icon={<Wifi className="w-4 h-4" />}
          label={tt('Testovať pripojenie')}
        />
        <ActionButton
          onClick={() =>
            confirmThen({
              title: tt('Zatvoriť otvorený doklad?'),
              description: tt(
                'Pošle príkaz FTCLOSE. Ak je na kase otvorený doklad, zatvorí sa - odblokuje to kasu po zlyhaní dokladu so zľavou.',
              ),
              run: () => recover.mutate({ data: { kasaId } }),
            })
          }
          pending={recover.isPending}
          icon={<AlertTriangle className="w-4 h-4" />}
          label={tt('Odomknúť kasu')}
          tone="amber"
        />
        <ActionButton
          onClick={() =>
            confirmThen({
              // FB-071. Legacy fires this on one click with no warning at all.
              title: tt('Vystaviť skutočný fiskálny doklad?'),
              description: tt(
                'Toto nie je simulácia. Kasa vystaví daňový doklad na 1,00 € za „Test tovar", ktorý nikto nezaplatil, a doklad bude treba stornovať.',
              ),
              run: () =>
                receipt.mutate({
                  data: {
                    kasaId,
                    orderId: 0,
                    orderNumber: 'TEST-001',
                    orderType: 'restaurant',
                    items: [{ name: 'Test tovar', quantity: 1, unitPrice: 1, vatRate: 5 }],
                    paymentMethod: 'cash',
                    total: 1,
                  },
                }),
            })
          }
          pending={receipt.isPending}
          icon={<CheckCircle2 className="w-4 h-4" />}
          label={tt('Test dokladu (1,00 €)')}
          tone="emerald"
        />

        {testResult && (
          <div
            data-test-result={testResult.ok ? 'ok' : 'failed'}
            className={cn(
              'mt-3 w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm',
              testResult.ok
                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400',
            )}
          >
            {testResult.ok ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {testResult.message}
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-border">
        <p className="text-xs text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5" />
          <T>Diagnostika - natívny TCP príkaz</T>
        </p>
        <div className="flex gap-2">
          <input
            value={rawCmd}
            onChange={(event) => setRawCmd(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && sendRaw(rawCmd)}
            placeholder="napr. FRRTC"
            aria-label={tt('Natívny príkaz')}
            className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-xs font-mono outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            onClick={() => sendRaw(rawCmd)}
            disabled={raw.isPending || !rawCmd.trim()}
            className="px-3 py-1.5 bg-muted hover:bg-muted/70 disabled:opacity-50 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
          >
            {raw.isPending && <Spinner className="w-3 h-3" />}
            <T>Odoslať</T>
          </button>
        </div>

        <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1.5">
          <EffectBadge effect={effectOf(rawCmd)} />
          {effectHint(effectOf(rawCmd))}
        </p>

        <div className="flex flex-wrap gap-1.5 mt-2">
          {DIAGNOSTIC_COMMANDS.map((command) => (
            <button
              key={command.cmd}
              type="button"
              onClick={() => sendPreset(command)}
              disabled={raw.isPending}
              title={command.consequence}
              className={cn(
                'px-2 py-1 border rounded text-[10px] font-mono transition-colors disabled:opacity-50',
                command.effect === 'reads'
                  ? 'bg-muted/50 border-border text-muted-foreground hover:text-foreground'
                  : 'bg-amber-500/10 border-amber-500/40 text-amber-500 hover:bg-amber-500/20',
              )}
            >
              {command.effect !== 'reads' && <AlertTriangle className="w-2.5 h-2.5 inline mr-1" />}
              {command.label}
            </button>
          ))}
        </div>

        {output && (
          <pre
            data-device-output
            className="mt-2 p-2 bg-background border border-border rounded-lg text-xs overflow-auto max-h-40 font-mono"
          >
            {output}
          </pre>
        )}

        <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
          <T>
            Ak FRLOC6466 vráti 0, kartové platby sú vypnuté. Zapnú sa príkazom FSLOC6466%1, potom
            treba kasu restartovať (FSRST). Po FTCARDSTART musí zákazník priložiť kartu a výsledok
            príde cez FTCARDINFO.
          </T>
        </p>
      </div>

      <div className="flex flex-col gap-2 pt-3">
        <button
          type="button"
          onClick={() =>
            confirmThen({
              title: tt('Resetovať pripojenie terminálu?'),
              description: tt(
                'Resetuje circuit breaker, ktorý po opakovaných zlyhaniach prestal kasu volať. Kasa sa začne volať znova.',
              ),
              run: () => resetBreaker.mutate({ data: { kasaId } }),
            })
          }
          disabled={resetBreaker.isPending}
          className="w-full flex items-center justify-center gap-2 bg-orange-600/80 hover:bg-orange-500/80 disabled:opacity-50 text-orange-50 font-semibold rounded-xl py-2.5 transition-colors"
        >
          {resetBreaker.isPending ? <Spinner className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
          <T>Resetovať pripojenie terminálu</T>
        </button>
        <button
          type="button"
          onClick={() =>
            confirmThen({
              title: tt('Odblokovať eKasu?'),
              description: tt(
                'Pošle FXEKASAUNBLOCK. Používa sa, keď kasa odmieta vystavovať doklady, pretože ju daňové zariadenie zablokovalo.',
              ),
              run: () => unblock.mutate({ data: { kasaId } }),
            })
          }
          disabled={unblock.isPending}
          className="w-full flex items-center justify-center gap-2 bg-red-700/70 hover:bg-red-600/70 disabled:opacity-50 text-red-50 font-semibold rounded-xl py-2.5 transition-colors"
        >
          {unblock.isPending ? <Spinner className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
          <T>Odblokovať eKasu (FXEKASAUNBLOCK)</T>
        </button>
      </div>

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          pending?.run()
          setPending(null)
        }}
        title={pending?.title ?? ''}
        description={pending?.description}
        confirmLabel={tt('Odoslať do pokladne')}
        variant="danger"
      />
    </>
  )
}

// ─── the settings both registers share ───────────────────────────────────────

function SharedSettingsSection() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // One request for the shared block and the restaurant register — legacy fires
  // two, because each section fetches for itself.
  const { data, isLoading } = useGetSettingsFiskal({ kasaId: 'restauracia' })

  const [form, setForm] = useState<SharedForm>(() => readShared(undefined))
  const [loadedFrom, setLoadedFrom] = useState<FiskalSettings | undefined>(undefined)
  if (data && data !== loadedFrom) {
    setLoadedFrom(data)
    setForm(readShared(data))
  }

  // Read straight off the response rather than through `readShared`, which no longer carries them:
  // this screen must not be able to write a rate, and a field it does not hold is a field its PUT
  // cannot carry. §9.327.
  const stored: Record<string, unknown> = { ...data }
  const storedDineIn = typeof stored.vatRateDineIn === 'string' ? stored.vatRateDineIn : ''
  const storedDelivery = typeof stored.vatRateDelivery === 'string' ? stored.vatRateDelivery : ''
  const storedFallback = typeof stored.defaultVatRate === 'string' ? stored.defaultVatRate : ''

  const set = <K extends keyof SharedForm>(key: K, value: SharedForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const save = usePutSettingsFiskalShared({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsFiskalQueryKey() })
        toast({
          title: tt('Zdieľané nastavenia uložené'),
          description: tt('Sadzby DPH a údaje podniku sa použijú pre obe kasy.'),
        })
      },
      onError: (error: unknown) => toast(failure(error)),
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-24">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4" data-shared-settings>
      {/*
        **The rates are read here and set on one screen - §9.327.**

        The owner, twice: *"I am pretty sure you can do it only in one place, not on two places these
        Sadzby DPH"*. §9.324 built the merged screen and left these three pickers behind, so the same
        three rates were editable in two places at once, which is the complaint rather than the fix.

        They are still *shown*, because a receipt is printed from them and somebody configuring a
        register needs to see which rates it will use without leaving the screen. Showing and editing
        are different things, and only one of them can live in two places safely.
      */}
      <div className="bg-card rounded-xl border border-border p-5 space-y-4" data-register-rates>
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Receipt className="w-4 h-4 text-muted-foreground" /> Sadzby DPH
        </h3>

        <dl className="space-y-2">
          <RateRow label={tt('DPH - konzumácia v reštaurácii (dine-in)')} rate={storedDineIn} />
          <RateRow label={tt('DPH - rozvoz a odber')} rate={storedDelivery} />
          <RateRow label={tt('DPH - predvolená sadzba')} rate={storedFallback} />
        </dl>

        <a
          href="/vat-categories"
          data-vat-categories-link
          className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        >
          <T>Zmeniť v Predaj a účtovanie - Sadzby DPH</T>
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      </div>

      <div className="bg-card rounded-xl border border-border p-5 space-y-4">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <T>Údaje podniku</T>
        </h3>
        <Field label={tt('Názov prevádzky')}>
          <TextInput
            value={form.restaurantName}
            onChange={(value) => set('restaurantName', value)}
            placeholder="PizzaMajstro"
            label={tt('Názov prevádzky')}
          />
        </Field>
        <Field label={tt('Adresa prevádzky')}>
          <TextInput
            value={form.restaurantAddress}
            onChange={(value) => set('restaurantAddress', value)}
            placeholder={tt('Hlavná 1, 811 01 Bratislava')}
            label={tt('Adresa prevádzky')}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={tt('IČO')}>
            <TextInput
              value={form.restaurantIco}
              onChange={(value) => set('restaurantIco', value)}
              placeholder="12345678"
              label={tt('IČO')}
            />
          </Field>
          <Field label={tt('DIČ')}>
            <TextInput
              value={form.restaurantDic}
              onChange={(value) => set('restaurantDic', value)}
              placeholder="2012345678"
              label={tt('DIČ')}
            />
          </Field>
        </div>
      </div>

      <button
        type="button"
        onClick={() => save.mutate({ data: form })}
        disabled={save.isPending}
        className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-xl py-2.5 transition-colors"
      >
        {save.isPending ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />}
        <T>Uložiť zdieľané nastavenia (pre obe kasy)</T>
      </button>
    </div>
  )
}

// ─── the rates, read-only - §9.327 ───────────────────────────────────────────

const SELECT_CLASSES =
  'w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

/**
 * One stored rate, shown rather than offered.
 *
 * **`VatSelect` and `RateWarning` used to live here and have moved**, whole, to
 * `manager-settings/vat-categories/RegisterRatePicker.tsx` - the owner asked twice for the rates to
 * be settable in one place and §9.324 had left them settable in two. The guard they call moved with
 * them rather than being deleted: `rateProblem` catches a rate banding onto a VAT letter configured
 * for a **different** rate, which is a receipt that looks right and registers the wrong tax (FB-070).
 *
 * <p>So this screen no longer warns, because it no longer offers a choice that could be wrong. The
 * warning is on the screen where the choice is now made, and the four e2e tests that assert it moved
 * to `vat-categories.spec.ts` with it.
 */
function RateRow({ label, rate }: { label: string; rate: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3" data-rate-row={label}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-semibold tabular-nums">
        {rate === '' ? <T>nenastavené</T> : `${rate} %`}
      </dd>
    </div>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

function ToggleCard({
  on,
  onChange,
  icon,
  tone,
  label,
  hint,
}: {
  on: boolean
  onChange: (next: boolean) => void
  icon: ReactNode
  tone: 'emerald' | 'violet'
  label: string
  hint: string
}) {
  const active = tone === 'emerald' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-violet-500/10 border-violet-500/30'
  const knob = tone === 'emerald' ? 'peer-checked:bg-emerald-500' : 'peer-checked:bg-violet-500'
  const text = tone === 'emerald' ? 'text-emerald-400' : 'text-violet-300'

  return (
    <div
      className={cn(
        'rounded-xl border p-4 flex items-start gap-4',
        on ? active : 'bg-muted/40 border-border',
      )}
    >
      <div
        className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
          on ? (tone === 'emerald' ? 'bg-emerald-500/20' : 'bg-violet-500/20') : 'bg-muted',
        )}
      >
        <span className={on ? text : 'text-muted-foreground'}>{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('font-semibold text-sm', on ? text : 'text-foreground')}>{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{hint}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer shrink-0">
        <input
          type="checkbox"
          checked={on}
          aria-label={label}
          onChange={(event) => onChange(event.target.checked)}
          className="sr-only peer"
        />
        <span
          className={cn(
            "w-11 h-6 bg-muted rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full",
            knob,
          )}
        />
      </label>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
      {hint && <p className="text-xs text-muted-foreground/80">{hint}</p>}
    </div>
  )
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  label,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: string
  label: string
}) {
  return (
    <input
      type={type}
      value={value}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
    />
  )
}

function ActionButton({
  onClick,
  pending,
  icon,
  label,
  tone,
}: {
  onClick: () => void
  pending: boolean
  icon: ReactNode
  label: string
  tone?: 'amber' | 'emerald'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className={cn(
        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
        tone === 'amber'
          ? 'bg-amber-700/60 hover:bg-amber-600/70 text-amber-50'
          : tone === 'emerald'
            ? 'bg-emerald-700/60 hover:bg-emerald-600/70 text-emerald-50'
            : 'bg-muted hover:bg-muted/70',
      )}
    >
      {pending ? <Spinner className="w-4 h-4" /> : icon}
      {label}
    </button>
  )
}

function EffectBadge({ effect }: { effect: CommandEffect }) {
  if (effect === 'reads') return <StatusBadge color="zinc" label={tt('iba čítanie')} />
  if (effect === 'writes') {
    return <StatusBadge color="amber" label={tt('mení kasu')} icon={AlertTriangle} />
  }
  return <StatusBadge color="red" label={tt('doklad / platba')} icon={AlertTriangle} />
}

function effectHint(effect: CommandEffect): string {
  if (effect === 'reads') return tt('Príkaz sa iba spýta a nič nezmení.')
  if (effect === 'writes') return tt('Príkaz zmení nastavenie kasy - pred odoslaním sa potvrdzuje.')
  return tt('Príkaz otvorí doklad alebo platbu - pred odoslaním sa potvrdzuje.')
}

function consequenceOf(cmd: string): string {
  const known = DIAGNOSTIC_COMMANDS.find((command) => cmd.includes(command.cmd))
  return known?.consequence ?? effectHint(effectOf(cmd))
}

// ─── reading an untyped response ─────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function readString(value: unknown, key: string): string | null {
  const field = asRecord(value)?.[key]
  return typeof field === 'string' ? field : null
}

function isOk(value: unknown): boolean {
  return asRecord(value)?.ok === true
}

function errorText(value: unknown): string | null {
  return readString(value, 'error')
}

function messageText(value: unknown): string | null {
  return readString(value, 'message')
}

function stringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value)
  } catch {
    return tt('Odpoveď sa nedá zobraziť.')
  }
}

function failure(error: unknown): { title: string; description: string; variant: 'destructive' } {
  return {
    title: tt('Chyba'),
    description: errorText(error) ?? messageText(error) ?? tt('Operácia sa nepodarila.'),
    variant: 'destructive',
  }
}
